import { DateRangePicker, Input, MultiSelect, Select, Text, type TypeSelectOption } from '@thefarmersfront/kitchen';

import clsx from 'clsx';
import dayjs from 'dayjs';

import { SearchFilter } from '~/components/common';

import { OPTION_ALL } from '~/constants';

import type {
  CollectingRequestFilterOptions,
  CollectingRequestFilterValues,
  CollectingRequestFilterValuesStatus,
} from '~/types/delivery-agency.types';

import styles from './CollectingRequestFilter.module.scss';

const CollectingRequestFilter = ({
  options,
  values,
  valuesStatus,
  onChange,
  onChangeStatus,
  onSearch,
  onReset,
}: {
  options: CollectingRequestFilterOptions;
  values: CollectingRequestFilterValues;
  valuesStatus: CollectingRequestFilterValuesStatus;
  onChange: (values: Partial<CollectingRequestFilterValues>) => void;
  onChangeStatus: (values: Partial<CollectingRequestFilterValuesStatus>) => void;
  onSearch: () => void;
  onReset: () => void;
}) => {
  return (
    <SearchFilter formId="collecting-request-search-form" onSubmit={onSearch} onReset={onReset}>
      <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
        <Text size="small" weight="semi-bold" element="p">
          키워드
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.searchKeywordTypes}
            value={values.keywordType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.keywordType?.value) return;
              onChange({ keywordType: value, keyword: '' });
              onChangeStatus({ keyword: 'default' });
            }}
          />
          <Input
            type="text"
            size="small"
            placeholder="입력"
            maxLength={50}
            value={values.keyword}
            status={valuesStatus.keyword}
            onChange={(e) => onChange({ keyword: e.target.value })}
            onBlur={(e) => onChange({ keyword: e.target.value.trim() })}
            onFocus={() => onChangeStatus({ keyword: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-date'])}>
        <Text size="small" weight="semi-bold" element="p">
          반품접수일
        </Text>
        <div className={styles['input-container']}>
          <DateRangePicker
            size="small"
            placeholder={{ start: '시작일', end: '종료일' }}
            minDate={dayjs().subtract(3, 'month').startOf('month').toDate()}
            maxDate={dayjs().add(1, 'day').endOf('day').toDate()}
            value={values.collectingRequestedDateRange}
            status={valuesStatus.collectingRequestedDateRange}
            onChange={(value) => onChange({ collectingRequestedDateRange: value })}
            onFocus={() => onChangeStatus({ collectingRequestedDateRange: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-status'])}>
        <Text size="small" weight="semi-bold" element="p">
          회수 상태
        </Text>
        <div className={styles['input-container']}>
          <MultiSelect
            size="small"
            placeholder="선택 (복수 선택 가능)"
            options={[OPTION_ALL, ...options.collectingRequestStatuses]}
            value={values.collectingRequestStatuses}
            allOption={OPTION_ALL}
            onChange={(_, value) => onChange({ collectingRequestStatuses: value ?? [] })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-product-handling'])}>
        <Text size="small" weight="semi-bold" element="p">
          회수한 상품 처리 방식
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={[OPTION_ALL, ...options.collectedProductHandlings]}
            value={values.collectedProductHandling}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ collectedProductHandling: value })}
          />
        </div>
      </div>
    </SearchFilter>
  );
};

export default CollectingRequestFilter;
